import { ReactElement, useCallback, useMemo, memo } from 'react';
import { CircularProgress } from '@mui/material';
import {
  Delete as DeleteIcon,
  ChangeCircle as ChangeStateIcon,
  ManageAccounts as ModifyAssignmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClone as DuplicateIcon,
  faBarcode as BardCodeIcon,
  faRoute as RouteIcon,
} from '@fortawesome/free-solid-svg-icons';

import {
  CollectItemStateTypes,
  PlanFeaturesTypes,
  RouteItemStateType,
} from '~globals/types/enums';
import { useAppSelector } from '~hooks/useAppSelector';
import { selectAuth } from '~store/slices/auth';
import { ItemExtended } from '~services/item/types';
import { isOnlyReadUser, isOnlyCreatorOrder } from '~utils/user';
import { getFeatureAvailableToDate } from '~utils/feature';
import { getUrlTrackingOrder, openUrlInNewTab } from '~utils/commons';
import { PATHS } from '~constants/paths';
import MenuActionsDataTable from '~components/MenuActionsDataTable';

import { useDashboardVariantV2CarrierContentInWarehouseTabContext } from '../../Context';
import { ITEM_KEY_ID } from '../../utils';

export interface DashboardVariantV2CarrierContentInWarehouseTabDataTableActionsMenuProps {
  data: ItemExtended;
  onExporTicket: (trackingCode: string) => void;
  onExportTicketMl: (id: string) => void;
  loadingExportTicket: boolean;
  loadingExportTicketMl: boolean;
}

const DashboardVariantV2CarrierContentInWarehouseTabDataTableActionsMenu = ({
  data,
  onExporTicket,
  onExportTicketMl,
  loadingExportTicket,
  loadingExportTicketMl,
}: DashboardVariantV2CarrierContentInWarehouseTabDataTableActionsMenuProps): ReactElement => {
  const navigate = useNavigate();
  const { user } = useAppSelector(selectAuth);

  const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);
  const isUserOnlyCreatorOrder = useMemo(
    () => isOnlyCreatorOrder(user),
    [user],
  );

  const {
    dialogs: {
      detailsOrder: { handleOpen: handleOpenDialogDetailsOrder },
      changeStateOrder: { handleOpen: handleOpenDialogChangeStateOrder },
      modifyAssignmentOrder: {
        handleOpen: handleOpenDialogModifyAssignmentOrder,
      },
      confirmDeleteOrder: { handleOpen: handleOpenConfirmDeleteOrder },
    },
  } = useDashboardVariantV2CarrierContentInWarehouseTabContext();

  const isCollected = useMemo(
    () =>
      data.itemStateTypeId === RouteItemStateType.New &&
      data.collectItemStateTypeId === CollectItemStateTypes.Validated,
    [data.collectItemStateTypeId, data.itemStateTypeId],
  );

  const handleRedirectToNewOrder = useCallback(
    (extraData?: Record<string, unknown>) => {
      navigate(PATHS.ORDERS.NEW, { state: extraData });
    },
    [navigate],
  );

  const enabledEdit = useMemo(
    () => !isUserOnlyReader && !isUserOnlyCreatorOrder && data.canModify,
    [isUserOnlyReader, isUserOnlyCreatorOrder, data.canModify],
  );

  const enabledChangeState = useMemo(
    () => !isUserOnlyReader && !isCollected,
    [isCollected, isUserOnlyReader],
  );

  const enabledExportTicketMl = useMemo(
    () => !isUserOnlyReader && data.sourceImportTypeId === 5 && !isCollected,
    [data.sourceImportTypeId, isCollected, isUserOnlyReader],
  );

  const enabledDeleted = useMemo(
    () => !isUserOnlyReader && data.canDelete && !isCollected,
    [data.canDelete, isCollected, isUserOnlyReader],
  );

  const enabledCancel = useMemo(
    () => !isUserOnlyReader && !isCollected,
    [isCollected, isUserOnlyReader],
  );

  return (
    <MenuActionsDataTable
      resource={{
        path: PATHS.ORDERS.BASE,
        id: data[ITEM_KEY_ID],
      }}
      items={{
        show: {
          enabled: true,
          onClick: () =>
            handleOpenDialogDetailsOrder({
              orderId: data[ITEM_KEY_ID],
              currentTab: null,
            }),
          divider: !enabledEdit,
        },
        edit: {
          enabled: enabledEdit,
          divider: !isCollected,
        },
      }}
      extraItems={[
        {
          enabled: enabledChangeState,
          label: 'Cambiar estado',
          Icon: <ChangeStateIcon color="success" />,
          onClick: () =>
            handleOpenDialogChangeStateOrder({
              itemsIds: [data[ITEM_KEY_ID]],
              excludeStateOption:
                data.collectItemStateTypeId !== null
                  ? undefined
                  : data.itemStateTypeId,
            }),
        },
        {
          enabled: enabledChangeState,
          label: 'Modificar asignación',
          Icon: <ModifyAssignmentIcon color="success" />,
          onClick: () =>
            handleOpenDialogModifyAssignmentOrder(data[ITEM_KEY_ID]),
          divider: true,
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.LimitNotPro,
          },
        },
        {
          enabled: !isCollected,
          label: 'Ver seguimiento de órden',
          Icon: <FontAwesomeIcon icon={RouteIcon} />,
          onClick: () => {
            openUrlInNewTab(getUrlTrackingOrder(data.trackingCode));
          },
          divider: true,
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.TrackingPortalModule,
          },
        },
        {
          enabled: !isCollected,
          label: 'Exportar etiqueta',
          Icon: loadingExportTicket ? (
            <CircularProgress color="primary" disableShrink size={20} />
          ) : (
            <FontAwesomeIcon icon={BardCodeIcon} />
          ),
          onClick: () => onExporTicket(data.trackingCode),
          divider:
            enabledEdit ||
            enabledDeleted ||
            enabledCancel ||
            enabledExportTicketMl,
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.MgmGenerateTickets,
            availableToDate: getFeatureAvailableToDate(
              PlanFeaturesTypes.MgmGenerateTickets,
            ),
          },
        },
        {
          enabled: enabledExportTicketMl,
          label: 'Exportar etiqueta de Mercado Libre',
          Icon: loadingExportTicketMl ? (
            <CircularProgress color="primary" disableShrink size={20} />
          ) : (
            <FontAwesomeIcon icon={BardCodeIcon} />
          ),
          onClick: () => onExportTicketMl(data[ITEM_KEY_ID]),
          divider: enabledEdit || enabledDeleted || enabledCancel,
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.MgmGenerateTickets,
            availableToDate: getFeatureAvailableToDate(
              PlanFeaturesTypes.MgmGenerateTickets,
            ),
          },
        },
        {
          enabled: enabledEdit && !isCollected,
          label: 'Duplicar',
          Icon: <FontAwesomeIcon icon={DuplicateIcon} />,
          onClick: () =>
            handleRedirectToNewOrder({ duplicateById: data[ITEM_KEY_ID] }),
          divider: enabledCancel || enabledDeleted,
        },
        {
          enabled: enabledDeleted,
          label: 'Eliminar',
          Icon: <DeleteIcon color="error" />,
          onClick: () => handleOpenConfirmDeleteOrder(data),
        },
      ]}
    />
  );
};

export default memo(
  DashboardVariantV2CarrierContentInWarehouseTabDataTableActionsMenu,
);
